import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Custom Component Imports
import Tabs from "../../component/Tabs";
import ListItem from "../../component/ListItem";
import CartButton from "../../component/CartButton";
import GlobalPopup from "../../component/Popup/GlobalPopup";
import SelectItem from "../../component/MenuCard/SelectItem";
import TableOrderCart from "./TableOrderCart";
import "./tableOrderWorkspace.css";

// Redux Action Imports
import { fetchMenuData, openGlobalPopup } from "../../redux/action";

const MenuPage = () => {
    // URL params
    const { tableName: urlTableName, otp } = useParams();
    
    // Fallback: Default "DIRECT_BILL" if tableName isn't in URL
    const tableName = urlTableName || "DIRECT_BILL"; 

    const [selectedTabId, setSelectedTab] = useState('');
    const [search, setSearch] = useState("");
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [, refreshAvailability] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const menuList = useSelector((state) => state?.user?.menu);
    const fullCartState = useSelector((state) => state?.user?.cart); 
    
    // Dynamic Table's Cart or Direct Bill Cart
    const cart = fullCartState?.[tableName] || {}; 
    const isTableOrder = Boolean(urlTableName && otp);

    useEffect(() => {
        dispatch(fetchMenuData());
    }, [dispatch]);

    useEffect(() => {
        const boundaries = (menuList?.categories || []).flatMap(category => category.items || [])
            .map(item => Date.parse(item.availabilityValidUntil)).filter(Number.isFinite);
        if (!boundaries.length) return;
        const next = Math.min(...boundaries);
        let retry;
        const refresh = () => {
            refreshAvailability(value => value + 1);
            dispatch(fetchMenuData());
        };
        const timer = setTimeout(() => {
            refresh();
            retry = setInterval(refresh, 30000);
        }, Math.max(0, Math.min(next - Date.now(), 2147483647)));
        const onVisible = () => { if (document.visibilityState === "visible" && Date.now() >= next) refresh(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => { clearTimeout(timer); clearInterval(retry); document.removeEventListener("visibilitychange", onVisible); };
    }, [menuList, dispatch]);

    // 1. Auto-select 1st category ID once menuList finishes loading
    useEffect(() => {
        if (menuList?.categories?.length > 0 && !selectedTabId) {
            setSelectedTab(menuList.categories[0].categoryId);
        }
    }, [menuList, selectedTabId]);

    const tabList = () => {
        const categories = menuList?.categories || [];
        return categories.map((val) => ({
            categoryId: val.categoryId,
            category: val.categoryName,
            isSelected: val.categoryId === selectedTabId,
        }));
    };

    // 2. Safe search filtering checking both 'name' and 'foodName'
    const selectedItems = () => {
        if (!menuList || !menuList.categories) return null;

        const activeCategory = menuList.categories.find(
            (cat) => cat.categoryId === selectedTabId
        ) || menuList.categories.find(
            (cat) => Array.isArray(cat.items) && cat.items.length > 0
        );

        if (!activeCategory) return null;

        const filteredItems = (activeCategory.items || []).filter((food) => {
            const itemName = food?.name || food?.foodName || '';
            return itemName.toLowerCase().includes(search.trim().toLowerCase());
        });

        return {
            ...activeCategory,
            items: filteredItems
        };
    };

    return (
        <div className={isTableOrder ? "table-order-workspace" : ""}>
            <div className={isTableOrder ? "cart-container table-order-menu" : "cart-container"}>
                <div className="lrSpace">
                    <h2>Place Order</h2>
                    <div className="place-order-header">
                        <a className="link app-back-btn" onClick={() => dispatch(openGlobalPopup("select-category"))}>See all categories</a>
                        {tableName === "DIRECT_BILL" && (
                          <a className="link app-back-btn" onClick={() => navigate("/bills", { state: { billStatus: "Draft" } })}>Draft Bill(s)</a>
                        )}                       
                        {!isTableOrder && <CartButton tableName={tableName} />}
                    </div>

                    {menuList && <>
                        <Tabs tabs={tabList()} tabSelect={setSelectedTab} />

                        <GlobalPopup popupKey="select-category" title="Select Category">
                            <Tabs tabs={tabList()} tabSelect={setSelectedTab} openInPopup={true} />
                        </GlobalPopup>

                        <input 
                            type="text" 
                            placeholder="Search items in selected category..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="search-input" 
                        />

                        <ListItem 
                            category={selectedItems()} 
                            showNote={false} 
                            ActionComponent={SelectItem}
                            respectSchedule
                            tableName={tableName} 
                        />
                    </>}
                </div>
            </div>
            {isTableOrder && (
              <TableOrderCart
                cart={cart}
                tableName={tableName}
                otp={otp}
                isOpen={isCartOpen}
                setOpen={setIsCartOpen}
              />
            )}
        </div>
    );
};

export default MenuPage;
